import { computed, nextTick, ref, watch, type Ref } from 'vue';
import { addDockTab, removeDockTab, type DockPane } from '../lib/dockTabs';
import {
  enforceTabLimit, nextTabOrder, persistTabs as writeTabs, readOpenTabs, readTabGroups,
  removeTab, removeTabsWhere, reorderTabs, sortedTabs, tabGroupPalette,
  type TabGroup, type UtilityTab, type WorkspaceTab
} from '../lib/workspaceTabs';

type Options = {
  storage: Storage;
  workspaceId: Ref<string | null>;
  dock: Ref<DockPane | null>;
  onActivate: (tab: WorkspaceTab) => Promise<unknown> | unknown;
  onNoActiveTab: () => void;
};

export function useWorkspaceTabs(options: Options) {
  const { storage, dock } = options;
  const workspaceTabs = ref<WorkspaceTab[]>(readOpenTabs(storage));
  const tabGroups = ref<TabGroup[]>(readTabGroups(storage));
  const activeTabId = ref<string | null>(storage.getItem('chronis.workspace.active-tab.v1'));
  const tabLimit = ref(Math.max(1, Number(storage.getItem('chronis.workspace.tab-limit.v1') ?? 15) || 15));
  const tabDensity = ref<'normal' | 'minimal'>(storage.getItem('chronis.workspace.tab-density.v1') === 'minimal' ? 'minimal' : 'normal');
  const tabContextMenu = ref<{ tabId: string; x: number; y: number } | null>(null);
  const draggedTabId = ref<string | null>(null);
  const sortedWorkspaceTabs = computed(() => sortedTabs(workspaceTabs.value));
  const visibleTabGroups = computed(() => tabGroups.value.map((group) => ({ ...group, tabs: sortedWorkspaceTabs.value.filter((tab) => tab.groupId === group.id) })).filter((group) => group.tabs.length));
  const ungroupedTabs = computed(() => sortedWorkspaceTabs.value.filter((tab) => !tab.groupId));

  function persistTabs() { writeTabs(storage, workspaceTabs.value, tabGroups.value, tabLimit.value, activeTabId.value); }
  function limitTabs() {
    const limited = enforceTabLimit(workspaceTabs.value, activeTabId.value, tabLimit.value);
    workspaceTabs.value = limited.tabs;
    activeTabId.value = limited.activeId;
  }
  function entityTab(id: string) { return workspaceTabs.value.find((tab) => tab.kind === 'entity' && tab.entityId === id); }
  function tabById(id: string) { return workspaceTabs.value.find((tab) => tab.id === id); }
  function addEntityTab(id: string) {
    if (!entityTab(id)) {
      workspaceTabs.value = [...workspaceTabs.value, { id, kind: 'entity', entityId: id, createdAt: Date.now(), order: nextTabOrder(workspaceTabs.value), groupId: null, pinned: false }];
      limitTabs();
    }
    activeTabId.value = id;
    persistTabs();
    nextTick(() => document.getElementById(`workspace-tab-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' }));
  }
  function addUtilityTab(utility: UtilityTab) {
    const id = `utility:${utility}`;
    if (!tabById(id)) {
      workspaceTabs.value = [...workspaceTabs.value, { id, kind: 'utility', utility, createdAt: Date.now(), order: nextTabOrder(workspaceTabs.value), groupId: null, pinned: false }];
      limitTabs();
    }
    activeTabId.value = id;
    persistTabs();
  }
  function clearTabs(clearGroups = false) {
    workspaceTabs.value = [];
    activeTabId.value = null;
    if (clearGroups) tabGroups.value = [];
    tabContextMenu.value = null;
    draggedTabId.value = null;
    dock.value = null;
    persistTabs();
  }
  function initializeForWorkspace(entityId: string | null) {
    workspaceTabs.value = entityId ? [{ id: entityId, kind: 'entity', entityId, createdAt: Date.now(), order: 0, groupId: null, pinned: false }] : [];
    activeTabId.value = entityId;
    persistTabs();
  }
  function restoreTabs(validEntityIds: ReadonlySet<string>, currentEntityId: string | null) {
    workspaceTabs.value = workspaceTabs.value.filter((tab) => tab.kind === 'utility' || Boolean(tab.entityId && validEntityIds.has(tab.entityId)));
    const restored = activeTabId.value && workspaceTabs.value.some((tab) => tab.id === activeTabId.value) ? activeTabId.value : currentEntityId;
    if (restored && !tabById(restored)) workspaceTabs.value = [{ id: restored, kind: 'entity', entityId: restored, createdAt: Date.now(), order: nextTabOrder(workspaceTabs.value), groupId: null, pinned: false }];
    activeTabId.value = restored;
    persistTabs();
    return restored;
  }
  function startTabDrag(id: string, event: DragEvent) {
    draggedTabId.value = id;
    event.dataTransfer?.setData('application/x-chronis-tab', id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }
  function reorderTabsOnDrop(targetId: string, event: DragEvent) {
    const sourceId = event.dataTransfer?.getData('application/x-chronis-tab') || draggedTabId.value;
    if (!sourceId) return;
    const ordered = reorderTabs(workspaceTabs.value, sourceId, targetId);
    if (!ordered) return;
    workspaceTabs.value = ordered;
    draggedTabId.value = null;
    persistTabs();
  }
  function dockTab(side: 'right' | 'bottom', event: DragEvent) {
    const id = event.dataTransfer?.getData('application/x-chronis-tab') || draggedTabId.value;
    const tab = id ? tabById(id) : null;
    if (!tab?.entityId) return;
    dock.value = addDockTab(dock.value, tab, side, tabLimit.value);
    draggedTabId.value = null;
  }
  function closeDock() { dock.value = null; }
  function activateDockTab(id: string) { if (dock.value?.tabs.some((tab) => tab.id === id)) dock.value.activeTabId = id; }
  function closeDockTab(id: string) { dock.value = removeDockTab(dock.value, id); }
  async function activateTab(tab: WorkspaceTab) {
    tabContextMenu.value = null;
    activeTabId.value = tab.id;
    if (tab.kind === 'entity') persistTabs();
    await options.onActivate(tab);
  }
  async function closeTab(id: string, event?: MouseEvent) {
    event?.stopPropagation();
    const result = removeTab(workspaceTabs.value, activeTabId.value, id);
    if (!result) return;
    workspaceTabs.value = result.tabs;
    activeTabId.value = result.activeId;
    persistTabs();
    if (!result.wasActive) return;
    if (result.next) await activateTab(result.next); else options.onNoActiveTab();
  }
  function closeTabsWhere(predicate: (tab: WorkspaceTab) => boolean) {
    const result = removeTabsWhere(workspaceTabs.value, activeTabId.value, predicate);
    workspaceTabs.value = result.tabs;
    activeTabId.value = result.activeId;
    persistTabs();
    if (result.next) void activateTab(result.next); else options.onNoActiveTab();
  }
  function openTabContextMenu(tab: WorkspaceTab, event: MouseEvent) {
    event.preventDefault(); event.stopPropagation();
    tabContextMenu.value = { tabId: tab.id, x: Math.min(event.clientX, window.innerWidth - 210), y: Math.min(event.clientY, window.innerHeight - 285) };
  }
  function toggleTabPin(id: string) { const tab = tabById(id); if (!tab) return; tab.pinned = !tab.pinned; persistTabs(); }
  function setTabGroup(id: string, groupId: string | null) { const tab = tabById(id); if (!tab) return; tab.groupId = groupId; persistTabs(); }
  function createTabGroup(id: string) {
    const name = window.prompt('Name der neuen Gruppe');
    if (!name?.trim()) return;
    const group = { id: crypto.randomUUID(), name: name.trim(), collapsed: false, color: tabGroupPalette[tabGroups.value.length % tabGroupPalette.length]! };
    tabGroups.value = [...tabGroups.value, group];
    setTabGroup(id, group.id);
  }
  function toggleTabGroup(id: string) { const group = tabGroups.value.find((item) => item.id === id); if (!group) return; group.collapsed = !group.collapsed; persistTabs(); }
  function updateTabGroupColor(id: string, event: Event) { const group = tabGroups.value.find((item) => item.id === id); if (!group) return; group.color = (event.target as HTMLInputElement).value; persistTabs(); }
  function toggleTabDensity() { tabDensity.value = tabDensity.value === 'normal' ? 'minimal' : 'normal'; storage.setItem('chronis.workspace.tab-density.v1', tabDensity.value); }
  function updateTabLimit(value: number) {
    tabLimit.value = Math.max(1, Math.floor(value) || 15);
    limitTabs();
    if (dock.value && dock.value.tabs.length > tabLimit.value) {
      const keep = [...dock.value.tabs].sort((a, b) => b.createdAt - a.createdAt).slice(0, tabLimit.value);
      dock.value.tabs = dock.value.tabs.filter((tab) => keep.some((item) => item.id === tab.id));
      if (!dock.value.tabs.some((tab) => tab.id === dock.value?.activeTabId)) dock.value.activeTabId = dock.value.tabs[0]?.id ?? '';
    }
    persistTabs();
  }
  function closeTabContextMenu() { tabContextMenu.value = null; }
  function tabContextCloseRight(id: string) { const index = sortedWorkspaceTabs.value.findIndex((tab) => tab.id === id); closeTabsWhere((tab) => sortedWorkspaceTabs.value.indexOf(tab) > index); }
  function tabContextCloseLeft(id: string) { const index = sortedWorkspaceTabs.value.findIndex((tab) => tab.id === id); closeTabsWhere((tab) => sortedWorkspaceTabs.value.indexOf(tab) < index); }
  function tabContextCloseOthers(id: string) { closeTabsWhere((tab) => tab.id !== id); }
  function openTabContextGroup(id: string) { createTabGroup(id); closeTabContextMenu(); }
  function setTabContextGroup(id: string, groupId: string | null) { setTabGroup(id, groupId); closeTabContextMenu(); }
  function toggleTabContextPin(id: string) { toggleTabPin(id); closeTabContextMenu(); }
  function closeEntityTab(event: MouseEvent, id: string) { void closeTab(id, event); }

  watch(options.workspaceId, closeTabContextMenu);
  return {
    workspaceTabs, tabGroups, activeTabId, tabLimit, tabDensity, tabContextMenu, draggedTabId,
    sortedWorkspaceTabs, visibleTabGroups, ungroupedTabs, persistTabs, entityTab, tabById,
    addEntityTab, addUtilityTab, clearTabs, initializeForWorkspace, restoreTabs, startTabDrag, reorderTabs: reorderTabsOnDrop,
    dockTab, closeDock, activateDockTab, closeDockTab, activateTab, closeTab, closeTabsWhere,
    openTabContextMenu, toggleTabPin, setTabGroup, createTabGroup, toggleTabGroup,
    updateTabGroupColor, toggleTabDensity, updateTabLimit, closeTabContextMenu,
    tabContextCloseRight, tabContextCloseLeft, tabContextCloseOthers,
    openTabContextGroup, setTabContextGroup, toggleTabContextPin, closeEntityTab
  };
}
